import React, { createContext, useCallback, useContext, useMemo, ReactNode, useState } from 'react'
import { httpsCallable } from 'firebase/functions'
import { SnackbarHelperContext } from '../../../contexts/SnackbarHelperContext'
import { firebase_functions } from '../../../common/firebaseConfig'
import { useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import Box from '@mui/material/Box'
import { captureEvent } from '../../../posthog'

interface BacktestContextProps {
  backtestSettingInputData: any
  setBacktestSettingInputData: React.Dispatch<React.SetStateAction<any | null>>
  backtestToView: string | undefined
  setBacktestToView: React.Dispatch<React.SetStateAction<string | undefined>>
  backtestListItemFlipped: boolean | undefined
  setBacktestListItemFlipped: React.Dispatch<React.SetStateAction<boolean | undefined>>
  backtestListItemFlipStatus: { [backtestId: string]: boolean }
  setBacktestListItemFlipStatus: React.Dispatch<
    React.SetStateAction<{ [backtestId: string]: boolean }>
  >
  runBacktest: (settings: any) => void
  dialogOpen: { type: string; data: any } | undefined
  setOpenDialog: React.Dispatch<React.SetStateAction<{ type: string; data: any } | undefined>>
  backtestInputIsValid: boolean
  setBacktestInputIsValid: React.Dispatch<React.SetStateAction<boolean>>
  closeBacktestDialog: () => void
}

// Create the contexts
const BacktestContext = createContext<BacktestContextProps | undefined>(undefined)

interface Props {
  children: ReactNode
}

export const BacktestProvider: React.FC<Props> = ({ children }) => {
  const {
    backtestSettingInputData,
    setBacktestSettingInputData,
    tradingHouse,
    closeBacktestDialog,
  } = useApplicationSuiteContext()

  const [backtestToView, setBacktestToView] = useState<string>()
  const [backtestListItemFlipped, setBacktestListItemFlipped] = useState<boolean>()
  const [dialogOpen, setOpenDialog] = useState<{ type: string; data: any }>()
  const [backtestListItemFlipStatus, setBacktestListItemFlipStatus] = useState<{
    [backtestId: string]: boolean
  }>({})
  const [backtestInputIsValid, setBacktestInputIsValid] = useState<boolean>(false)

  const { setSnackbarSettings } = useContext(SnackbarHelperContext)

  const executeBacktestCallable = useMemo(
    () => httpsCallable(firebase_functions, 'patternbacktest'),
    []
  )

  const runBacktest = useCallback(
    async (settings: any) => {
      if (settings) {
        settings.tradingHouse = tradingHouse

        setSnackbarSettings({
          severity: 'info',
          message: <Box>Starting backtest...</Box>,
          autoHideDuration: 120000,
          anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
        })

        if (closeBacktestDialog) {
          closeBacktestDialog()
        }
        const response = await executeBacktestCallable(settings)
        if (response?.data) {
          const data: any = response.data
          if (data.status >= 400) {
            setSnackbarSettings({
              severity: 'error',
              message: data.message,
              autoHideDuration: 10000,
            })
          } else if (data.status >= 200) {
            captureEvent('backtest_started', {
              exit_strategy: settings.generateTrades?.exitStrategy,
              trade_ratio_count: settings.generateTrades?.tradeRatios?.length ?? 0,
              is_public: Boolean(settings.isPublic),
            })
            setSnackbarSettings({
              severity: 'success',
              message: <Box>{data.message}</Box>,
              autoHideDuration: 20000,
              anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
            })
          }
        }
      }
    },
    [tradingHouse, setSnackbarSettings, closeBacktestDialog, executeBacktestCallable]
  )

  const closeBacktestDialogStable = useMemo(
    () => closeBacktestDialog || (() => {}),
    [closeBacktestDialog]
  )

  const contextValue = useMemo(
    () => ({
      backtestInputIsValid,
      setBacktestInputIsValid,
      backtestListItemFlipStatus,
      setBacktestListItemFlipStatus,
      dialogOpen,
      setOpenDialog,
      backtestToView,
      setBacktestToView,
      setBacktestSettingInputData,
      backtestSettingInputData,
      backtestListItemFlipped,
      setBacktestListItemFlipped,
      runBacktest,
      closeBacktestDialog: closeBacktestDialogStable,
    }),
    [
      backtestInputIsValid,
      backtestListItemFlipStatus,
      dialogOpen,
      backtestToView,
      setBacktestSettingInputData,
      backtestSettingInputData,
      backtestListItemFlipped,
      runBacktest,
      closeBacktestDialogStable,
    ]
  )

  return <BacktestContext.Provider value={contextValue}>{children}</BacktestContext.Provider>
}

// Custom hook to use this contexts
export const useBacktestContext = (): BacktestContextProps => {
  const context = useContext(BacktestContext)
  if (!context) {
    throw new Error('useBacktestContext must be used within a BacktestProvider')
  }
  return context
}
